import {
  AlertStatus,
  AlertType,
  DestinationTypeEnum,
  DetectionType,
  LogPullerTypeEnum,
  LogStreamType,
  Permission,
  SchemasInput,
  Severity,
  SortDirEnum,
} from 'Generated/schema';
import slackLogo from 'Assets/slack-minimal-logo.svg';
import discordLogo from 'Assets/discord-minimal-logo.svg';
import msTeamsLogo from 'Assets/ms-teams-minimal-logo.svg';
import opsgenieLogo from 'Assets/opsgenie-minimal-logo.svg';
import jiraLogo from 'Assets/jira-minimal-logo.svg';
import gcpPubSubLogo from 'Assets/logProviders/gcp-pubsub.svg';
import githubLogo from 'Assets/github-minimal-logo.svg';
import githubLogoLight from 'Assets/github-minimal-logo-light.svg';
import pagerDutyLogo from 'Assets/pagerduty-minimal-logo.svg';
import rapid7ShortLogo from 'Assets/rapid7-short-logo.svg';
import snsLogo from 'Assets/aws-sns-minimal-logo.svg';
import sqsLogo from 'Assets/aws-sqs-minimal-logo.svg';
import asanaLogo from 'Assets/asana-minimal-logo.svg';
import blinkOpsLogo from 'Assets/blinkops-logo.svg';
import customWebhook from 'Assets/custom-webhook-minimal-logo.svg';
import customWebhookLight from 'Assets/custom-webhook-minimal-logo-light.svg';
import expelLogo from 'Assets/expel-logo.svg';
import incidentIoLogo from 'Assets/incident-io-logo.svg';
import mindflowLogo from 'Assets/mindflow-logo.svg';
import mindflowLogoLight from 'Assets/mindflow-logo-light.svg';
import splunkLogo from 'Assets/splunk-logo.svg';
import tinesLogo from 'Assets/tines-logo.svg';
import tinesLogoLight from 'Assets/tines-logo-light.svg';
import extremeAlertVolume from 'Assets/alertVolume/extreme.svg';
import highAlertVolume from 'Assets/alertVolume/high.svg';
import lowAlertVolume from 'Assets/alertVolume/low.svg';
import noneAlertVolume from 'Assets/alertVolume/none.svg';
import accountManaged from 'Assets/managedStatuses/account.svg';
import correlationRule from 'Assets/ruleTypes/correlation.svg';
import pythonRule from 'Assets/ruleTypes/python.svg';
import scheduledRule from 'Assets/ruleTypes/scheduled.svg';
import simpleRule from 'Assets/ruleTypes/simple.svg';
import pantherManaged from 'Assets/panther-minimal-logo.svg';
import selfManaged from 'Assets/managedStatuses/self.svg';
import torqLogo from 'Assets/torq-logo.svg';
import torqLogoLight from 'Assets/torq-logo-light.svg';
import { BadgeVariant } from 'pounceV2/src/components/Badge/Badge';
import type { Theme as MuiTheme } from '@mui/material/styles';
import { ListLogSourceSortFieldsEnum } from './ListLogSourceSortFieldsEnum';
import { ListDataSchemasSortFieldsEnum } from './ListDataSchemasSortFieldsEnum';

export const __DEV__ = process.env.NODE_ENV !== 'production';

type PantherConfig = {
  AWS_REGION: string;
  CAN_USE_AI_APIS: string;
  DEBUG: string;
  NODE_ENV: string;
  PANTHER_VERSION: string;
  PUBLIC_CORE_API_HOST: string;
  WEB_APPLICATION_GRAPHQL_API_ENDPOINT: string;
  WEB_APPLICATION_USER_POOL_CLIENT_ID: string;
  WEB_APPLICATION_USER_POOL_ID: string;
  REFRESH_TOKEN_EXPIRATION_HOURS: number;
  FEDRAMP_ENABLED: string;
  ENVIRONMENT?: string;
  SENTRY_DSN?: string;
  WEB_APPLICATION_USER_POOL_HOST?: string;
  PYLON_APP_ID?: string;
  COGNITO_USER_POOL_PASSWORD_MIN_LENGTH: number;
  GITHUB_APP_SIGNING_PUBLIC_KEY?: string;
};

// read the NodeJS-injected configuration and parse it back from JSON -> JS object. During development,
// the NodeJS server doesn't contribute in that at all, but instead the HtmlWebpackLoader injects
// them directly. There is a long-run inconsistency with EJS templates and webpack's ejs-loader,
// where the "symbol" for unescaped entities is different when using ejs-loader (development) and
// when you are just using EJS templates without a webpack loader (production). Just because we
// don't know whether we arrived here from development or production, we are doing the `&quot;`
// replace you see to make sure we convert an escaped entity back to its original value
export const PANTHER_CONFIG: PantherConfig = document.getElementById('__PANTHER_CONFIG__')
  ? Object.freeze(
      JSON.parse(
        document
          .getElementById('__PANTHER_CONFIG__')
          .innerHTML.toString()
          .replace(/&quot;/g, '"'),
      ),
    )
  : {};

/**
 * Contains all the split.io feature flags (named "treatments") names
 * used in the front-end application.
 *
 * For details how to use feature flags at Panther, see [Our Guide](https://www.notion.so/pantherlabs/Feature-Flag-Usage-edb9c6ed62f3470aa337ca6016adf9ca)
 * For more on Split.io treatments, see [Split's docs](https://help.split.io/hc/en-us/articles/360020523792-Create-a-split)
 */
export const SPLIT_FLAGS = {
  ENABLE_MINDFLOW_ALERT_DESTINATION: 'EnableMindflowAlertDestination',
  ENABLE_SPLUNK_ALERT_DESTINATION: 'EnableSplunkAlertDestination',
  ENABLE_ALERT_RBAC_LOG_TYPES: 'EnableAlertRbacLogTypes',
  ENABLE_ASYNC_BULK_UPLOADS: 'EnableAsyncBulkUploads',
  ENABLE_TEXT_SEARCH: 'EnableTextSearch',
  // Processing team flags
  ENABLE_MICROSOFT_365_PLAN_SELECTION: 'EnableMicrosoft365PlanSelection',
  ENABLE_HIDE_SIMPLE_DETECTIONS: 'HideSimpleDetections',
  ENABLE_DATA_REPLAY_WITHOUT_DEPLOY: 'DataReplayWithoutDeploy',

  // Content Catalog (Configure)
  ENABLE_CONTENT_CATALOG: 'EnableContentCatalog',

  // Correlation Rules
  ENABLE_CORRELATION_RULES: 'EnableCorrelationRules',

  // Signals
  ENABLE_SIGNALS: 'EnableSignals',

  // Whether to show the Salesforce domain type selection on the Salesforce log source page
  ENABLE_SALESFORCE_LOGS_SOURCE_DOMAIN_TYPE: 'EnableSalesforceLogsSourceDomainType',

  // Gates the Log Forwarder (on-prem agent / PLF) log source onboarding tile. The Split flag is
  // owned by the platform team and may not exist yet on every environment.
  PLF_SOURCE_ENABLED: 'PLFSourceEnabled',

  // Gates the OTLP (OpenTelemetry Protocol) log source onboarding tile. The Split flag is
  // owned by the platform team and may not exist yet on every environment.
  OTLP_SOURCE_ENABLED: 'OTLPSourceEnabled',

  // Gates the STIX/TAXII enrichment onboarding tile, wizard routes and details Collections tab.
  // The Split flag may not exist yet on every environment.
  STIX_TAXII_ENRICHMENT_ENABLED: 'StixTaxiiEnrichmentEnabled',

  // AI
  ENABLE_AI_AUTO_RESOLVE: 'EnableAIAutoResolve',
  ENABLE_AI_DATALAKE_REPLAY: 'EnableAIDatalakeReplay',
  ENABLE_AI_DETECTION_AUTHOR: 'EnableAiDetectionAuthor',
  ENABLE_AI_DETECTION_OVERVIEW: 'EnableAIDetectionOverview',
  ENABLE_AI_KENDRA_SETTINGS: 'EnableAIKendraSettings',
  ENABLE_AI_MCP_SERVERS: 'EnableAIMcpServers',
  ENABLE_AI_PANTHERFLOW_QUERY_GENERATION: 'EnableAiPantherflowQueryGeneration',
  ENABLE_AI_SQL_QUERY_GENERATION: 'EnableAiSqlQueryGeneration',
  ENABLE_AI_PRIVACY_CONTROLS: 'EnableAIPrivacyControls',
  ENABLE_AI_SKILLS: 'EnableAISkills',
  ENABLE_AI_SUMMARY_FEEDBACK: 'AISummaryFeedback',
  ENABLE_AI_LANDING_PAGE: 'AILandingPage',
  ENABLE_CHAT_HISTORY: 'EnableChatHistory',
  // PantherFlow
  ENABLE_PANTHERFLOW: 'Pantherflow',
  ENABLE_PANTHERFLOW_VISUALIZATIONS: 'EnablePantherflowVisualizations',

  ENABLE_REST_PLAYGROUND: 'EnableRestPlayground',
  ENABLE_AI_INFER_SCHEMA: 'EnableAiInferSchema',

  ENABLE_CUSTOM_DASHBOARDS: 'EnableCustomDashboards',

  // Search page live selection 2-way sync
  ENABLE_PANTHER_FLOW_LIVE_SELECTION_TWO_WAY_SYNC: 'EnablePantherFlowLiveSelectionTwoWaySync',

  ENABLE_GTI_ENRICHMENT: 'EnableGTIEnrichment',
  ENABLE_VIRUSTOTAL_ENRICHMENT: 'EnableVirusTotalEnrichment',

  PROFILE_SETTINGS_PAGE: 'ProfileSettingsPage',

  ENABLE_DASHBOARD_INPUTS: 'DashboardInputs',

  LIGHT_MODE: 'LightMode',

  USAGE_DASHBOARD: 'UsageDashboard',

  // by default in the log sources analytics page we show both a breakdown
  // of [events processed|bytes processed|data stored] per log type, as well as a
  // total (calculated from the breakdown) across all log types. This may be
  // innaccurate for customers with large amounts of log sources
  // (due to a limitation in AWS CloudWatch). To show a more accurate representation
  // we can turn this feature flag on which will show only the total bytes processed
  // instead of a breakdown. So it is a tradeoff of data dimensions for accuracy.
  ACCURATE_BYTES_PROCESSED: 'AccurateBytesProcessed',
};

// Panther AI
export const PANTHER_AI_NAME = 'Panther AI';

export const FEDRAMP_ENABLED = PANTHER_CONFIG.FEDRAMP_ENABLED === 'true';

// the date after which new POCs should display the onboarding guide
export const ONBOARDING_GUIDE_POV_RELEASE_DATE = '2022-11-01';

export const DEFAULT_REPLAY_DATA_HORIZON_DAYS = 30;
export const DAY_IN_MILLISECONDS = 86400000;

export enum LogTransportMethodsEnum {
  's3' = 'aws-s3',
  'eventbridge' = 'amazon-eventbridge',
  'amazonSecurityLake' = 'amazon-security-lake',
  'logPulling' = 'log-pulling',
  'sqs' = 'aws-sqs',
  'cloudwatch' = 'aws-cloudwatch-logs',
  'azureEventhub' = 'azure-eventhub',
  'gcs' = 'gcp-gcs',
  'gcpPubSub' = 'gcp-pubsub',
  'azureBlob' = 'azure-blob',
  'http' = 'http-ingest',
  'otlp' = 'otlp-ingest',
  'logForwarder' = 'log-forwarder',
}

export enum EnrichmentSourcesEnum {
  'custom' = 'custom',
  'anomali' = 'anomali',
  'otx' = 'otx',
  'greynoise' = 'greynoise',
  'gtiIoCStream' = 'googlethreatintelligence',
  'virustotalIoCStream' = 'virustotal',
  'taxii' = 'taxii',
}

export enum CustomEnrichmentMethodEnum {
  'query' = 'query',
  'manual' = 'manual',
  's3' = 's3',
  'gcs' = 'gcs',
}

export type LogSourceType =
  | Exclude<LogTransportMethodsEnum, LogTransportMethodsEnum.logPulling>
  | LogPullerTypeEnum;

export type ListLogSourceDropdownFilterValues = {
  logTypes?: string[];
  status?: 'Healthy' | 'Unhealthy';
};

export type ListLogSourceInlineFilterValues = {
  sortDir?: SortDirEnum;
  sortBy?: ListLogSourceSortFieldsEnum;
  contains?: string;
};

export type ListLogSourceFilterValues = ListLogSourceInlineFilterValues &
  ListLogSourceDropdownFilterValues;

export type ListDataSchemasInlineFiltersValues = Pick<SchemasInput, 'contains'> & {
  sortBy?: ListDataSchemasSortFieldsEnum;
  sortDir?: SortDirEnum;
};

export type ListSchemasURLParams = ListDataSchemasInlineFiltersValues & SchemasInput;

export const AWS_ACCOUNT_ID_REGEX = new RegExp('^\\d{12}$');

// Matches a full s3 object path, including the file name and extension.
// ex. 's3://bucket/path/to/file.txt'
export const S3_OBJECT_PATH_REGEX = new RegExp('^s3://([^/]+)/(.+\\.([^/]+))$');

// Matches a full gcs object path, including the file name and extension.
// ex. 'gs://bucket/path/to/file.txt'
export const GCS_OBJECT_PATH_REGEX = new RegExp('^gs://([^/]+)/(.+\\.([^/]+))$');

export const S3_BUCKET_NAME_REGEX = new RegExp(
  '(?=^.{3,63}$)(^(([a-z0-9]|[a-z0-9][a-z0-9\\-]*[a-z0-9])\\.)*([a-z0-9]|[a-z0-9][a-z0-9\\-]*[a-z0-9])$)',
);

export const INCLUDE_DIGITS_REGEX = new RegExp('(?=.*[0-9])');

export const INCLUDE_LOWERCASE_REGEX = new RegExp('(?=.*[a-z])');

export const SOURCE_LABEL_REGEX = new RegExp('^[a-zA-Z0-9- ]+$');

export const BUS_NAME_REGEX = new RegExp('^[^\\s]*aws\\.partner(/[\\.\\-_A-Za-z0-9]+){2,}[^\\s]*$');

export const ROLE_ARN_REGEX = new RegExp('^arn:(aws|aws-cn|aws-us-gov):iam::\\d{12}:role\\/\\S+$');

export const INCLUDE_UPPERCASE_REGEX = new RegExp('(?=.*[A-Z])');

export const INCLUDE_SPECIAL_CHAR_REGEX = new RegExp('[^\\d\\sA-Za-z]');

export const CHECK_IF_HASH_REGEX = new RegExp('[a-f0-9]{32}');

export const ARN_REGEX = new RegExp('arn:aws:\\S+:\\d+:\\w+/\\S+');

export const SQS_ARN_REGEX = new RegExp('^arn:aws:sqs:\\S+:\\d+:\\S+$');

export const DOMAIN_REGEX = new RegExp('^[a-z0-9]+([-.]{1}[a-z0-9]*)+[.[a-z]{2,5}]?$');

export const CROWDSTRIKE_EVENT_STREAMS_APP_ID_REGEX = new RegExp(
  '^[a-zA-Z][a-zA-Z0-9_-]{0,30}[a-zA-Z0-9]$',
);

/**
 * Expects a single subdomain within a fully qualifed domain name
 *
 * Allows subdomains like the following between brackets (composed of lowercase alphanumeric and dashes):
 * [abc-def-ghi].domain.com
 * [1234567890].domain.com
 * [00-abc-00].domain.com
 * [a].domain.com
 *
 * Will not allow any subdomains that end or start with a dash:
 * [-0].domain.com
 * [0-].domain.com
 * [-].domain.com
 */
export const SUBDOMAIN_REGEX = new RegExp('^(?:[a-z0-9]{1}[a-z0-9.-]*[a-z0-9]{1}|[a-z0-9]{1})$');

// Allows any string that DOESN'T contain the following characters: < > & "
export const NON_HTML_ENTITY_REGEX = new RegExp('^[^<>&"]+$');

export const DEFAULT_POLICY_FUNCTION =
  'def policy(resource):\n    # Return False if the resource is non-compliant, which will trigger alerts.\n    return True';

export const DEFAULT_RULE_FUNCTION =
  'def rule(event):\n    # Return True to match the log event and trigger an alert.\n    return False';

export const SCHEDULED_RULE_FUNCTION =
  'def rule(event):\n    # (Optional) Return True to match the log event and trigger an alert.\n    # If not declared, this defaults to True and creates alerts on all events in the query result.\n    return True';

/**
 * DEFAULT CORRELATION RULE TEMPLATES
 */

export const DEFAULT_CORRELATION_RULE_YAML_SEQUENCE = `Sequence: # Create a list of rules or signals to correlate (order matters)
    - ID: MyFirstRule
      RuleID: My.Rule.1
      MinMatchCount: 1
      # MaxMatchCount:
      # Absence: true # makes sure the step did not happen
    - ID: MySecondRule
      RuleID: My.Rule.2
      MinMatchCount: 1
      # MaxMatchCount:
      # Absence: true # makes sure the step did not happen`;

export const DEFAULT_CORRELATION_RULE_YAML_TRANSITIONS = `Transitions: # Add conditions for each transition, if needed (otherwise, the sequence will use any matches)
    - ID: Match first rule and second rule on username
      From: MyFirstRule # ID from sequence step
      To: MySecondRule # ID from sequence step
      # WithinTimeFrameMinutes: # set max amount of time for this transition to occur
      Match:
        - On: p_any_usernames # specify a key path for the transition to match on
          # From: # use 'From' and 'To' if the key paths in the 'From step' and the 'To step' will be different
          # To:`;

export const DEFAULT_CORRELATION_RULE_YAML_GROUP = `Group: # Create a list of rules or signals to correlate (order doesn't matter)
    - ID: MyFirstRule
      RuleID: My.Rule.1
      MinMatchCount: 1
      # MaxMatchCount:
      # Absence: true # makes sure the step did not happen
    - ID: MySecondRule
      RuleID: My.Rule.2
      MinMatchCount: 1
      # MaxMatchCount:
      # Absence: true # makes sure the step did not happen`;

export const DEFAULT_CORRELATION_RULE_YAML_MATCH_CRITERIA = `MatchCriteria: # Define fields that must have matching values in order for rule to pass
    field_name:
    - GroupID: MyFirstRule
      Match: # Some value, e.g., "p_alert_context.ip"
    - GroupID: MySecondRule
      Match: # Some value, e.g., "p_alert_context.ip"`;

export const DEFAULT_CORRELATION_RULE_YAML_EVALUATION_ORDER = `EventEvaluationOrder: Chronological`;

export const DEFAULT_CORRELATION_RULE_YAML_SCHEDULE = `Schedule:
    RateMinutes: 15
    # CronExpression: # set if you need a schedule other than minutes
    TimeoutMinutes: 2`;

export const DEFAULT_CORRELATION_RULE_YAML_LOOKBACK_MINUTES = `LookbackWindowMinutes: 60 # the amount of time this correlation rule should lookback over when evaluating rule and signal matches`;

export const DEFAULT_TITLE_FUNCTION =
  "def title(event):\n    # (Optional) Return a string which will be shown as the alert title.\n    # If no 'dedup' function is defined, the return value of this method will act as deduplication string.\n    return ''";

export const DEFAULT_DEDUP_FUNCTION =
  "# def dedup(event):\n    #  (Optional) Return a string which will be used to deduplicate similar alerts.\n    # return ''";

export const DEFAULT_ALERT_CONTEXT_FUNCTION =
  "# def alert_context(event):\n    #  (Optional) Return a dictionary with additional data to be included in the alert sent to the SNS/SQS/Webhook destination\n    # return {'key':'value'}";

export const RESOURCE_TYPES = [
  'AWS.ACM.Certificate',
  'AWS.CloudFormation.Stack',
  'AWS.CloudTrail',
  'AWS.CloudTrail.Meta',
  'AWS.CloudWatch.LogGroup',
  'AWS.Config.Recorder',
  'AWS.Config.Recorder.Meta',
  'AWS.DynamoDB.Table',
  'AWS.EC2.AMI',
  'AWS.EC2.Instance',
  'AWS.EC2.NetworkACL',
  'AWS.EC2.SecurityGroup',
  'AWS.EC2.Volume',
  'AWS.EC2.VPC',
  'AWS.ECS.Cluster',
  'AWS.EKS.Cluster',
  'AWS.ELBV2.ApplicationLoadBalancer',
  'AWS.GuardDuty.Detector',
  'AWS.GuardDuty.Detector.Meta',
  'AWS.IAM.Group',
  'AWS.IAM.Policy',
  'AWS.IAM.Role',
  'AWS.IAM.RootUser',
  'AWS.IAM.User',
  'AWS.KMS.Key',
  'AWS.Lambda.Function',
  'AWS.PasswordPolicy',
  'AWS.RDS.Instance',
  'AWS.Redshift.Cluster',
  'AWS.Route53.HostedZone',
  'AWS.Route53Domains',
  'AWS.S3.Bucket',
  'AWS.WAF.Regional.WebACL',
  'AWS.WAF.WebACL',
] as const;

export const SlackLogTypesEnterprise = ['Slack.AuditLogs'];
export const SlackLogTypesStandard = ['Slack.AccessLogs', 'Slack.IntegrationLogs'];

export enum SlackPlansEnum {
  'enterprise' = 'Enterprise+',
  'standard' = 'Pro/Business+',
}

export enum GoogleAppsEnum {
  'access_evaluation' = 'Access Evaluation',
  'access_transparency' = 'Access Transparency',
  'admin' = 'Admin Activity',
  'admin_data_action' = 'Admin Data Action',
  'assignments' = 'Assignments',
  'calendar' = 'Calendar',
  'chat' = 'Chat Activity',
  'chrome' = 'Chrome',
  'classroom' = 'Classroom',
  'cloud_search' = 'Cloud Search',
  'contacts' = 'Contacts',
  'context_aware_access' = 'Context-Aware Access',
  'data_migration' = 'Data Migration',
  'data_studio' = 'Data Studio',
  'directory_sync' = 'Directory Sync',
  'drive' = 'Drive',
  'gcp' = 'GCP',
  'gemini_in_workspace_apps' = 'Gemini for Workspace',
  'gmail' = 'Gmail',
  'graduation' = 'Graduation',
  'groups' = 'Groups Activity',
  'groups_enterprise' = 'Enterprise Groups Activity',
  'keep' = 'Keep',
  'ldap' = 'Secure LDAP',
  'login' = 'Login Activity',
  'meet' = 'Meet',
  'meet_hardware' = 'Meet Hardware',
  'mobile' = 'Mobile Audit',
  'profile' = 'Profile',
  'rules' = 'Rules Activity',
  'saml' = 'SAML Activity',
  'takeout' = 'Takeout',
  'tasks' = 'Tasks',
  'token' = 'Token Activity',
  'user_accounts' = 'User Accounts Activity',
  'vault' = 'Vault',
}

export enum WorkdayMethodEnum {
  Activity = 'Workday.Activity',
  SignOnAttempt = 'Workday.SignOnAttempt',
}

// Section with generic and specific doc URLs
export const AWS_REGIONS = [
  'us-east-1',
  'us-east-2',
  'us-west-1',
  'us-west-2',
  'af-south-1',
  'ap-east-1',
  'ap-south-1',
  'ap-northeast-1',
  'ap-northeast-2',
  'ap-northeast-3',
  'ap-southeast-1',
  'ap-southeast-2',
  'ca-central-1',
  'cn-north-1',
  'cn-northwest-1',
  'eu-central-1',
  'eu-west-1',
  'eu-west-2',
  'eu-west-3',
  'eu-south-1',
  'eu-north-1',
  'me-south-1',
  'sa-east-1',
  'us-gov-east-1',
  'us-gov-west-1',
] as const;

export const PANTHER_DOCS_LINK = `https://docs.panther.com`;

export const PANTHER_ENTERPRISE_SUPPORT_LINK =
  'https://panther-labs.atlassian.net/servicedesk/customer/portal/2/group/-1';

export const PANTHER_ANALYSIS_TOOL_REPO_URL = 'https://github.com/panther-labs/panther-analysis';

export const DATA_EXPLORER_DOC_URL = `${PANTHER_DOCS_LINK}/data-analytics/data-explorer`;
export const DESTINATIONS_DOC_URL = `${PANTHER_DOCS_LINK}/alerts/destinations`;
export const JIRA_DESTINATION_DOC_URL = `${DESTINATIONS_DOC_URL}/jira`;
export const JIRA_DATA_CENTER_DESTINATION_DOC_URL = `${DESTINATIONS_DOC_URL}/jira-data-center`;
export const DETECTIONS_DOC_URL = `${PANTHER_DOCS_LINK}/detections`;
export const PYPANTHER_DOC_URL = `${PANTHER_DOCS_LINK}/detections/pypanther`;
export const WRITING_DETECTIONS_DOC_URL = `${PANTHER_DOCS_LINK}/writing-detections`;
export const NEW_DETECTION_RULES_DOC_URL = `${PANTHER_DOCS_LINK}/writing-detections/rules`;
export const NEW_DETECTION_POLICIES_DOC_URL = `${PANTHER_DOCS_LINK}/writing-detections/policies`;
export const NEW_DETECTION_SCHEDULED_QUERIES_DOC_URL = `${PANTHER_DOCS_LINK}/data-analytics/scheduled-queries`;
export const MITRE_ATTACK_DOC_URL = `${PANTHER_DOCS_LINK}/detections/report-mapping#how-to-use-the-mitre-att-and-ck-r-feature-in-panther`;
export const ALERTS_DOC_URL = `${PANTHER_DOCS_LINK}/alerts/alert-management`;
export const DETECTION_PACKS_DOC_URL = `${PANTHER_DOCS_LINK}/writing-detections/detection-packs`;
export const DETECTION_GLOBAL_HELPERS_DOC_URL = `${PANTHER_DOCS_LINK}/writing-detections/globals`;
export const DATA_MODELS_DOC_URL = `${PANTHER_DOCS_LINK}/writing-detections/data-models`;
export const DATA_ONBOARDING_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding`;
export const DATA_TRANSPORTS_DOC_URL = `${DATA_ONBOARDING_DOC_URL}/data-transports`;
export const S3_ROLE_POLICY_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/data-transports/s3#manual-iam-role-creation-additional-steps`;
export const CLOUD_ACCOUNTS_DOC_URL = `${PANTHER_DOCS_LINK}/cloud-scanning`;
export const CWL_MANUALLY_IAM_ROLE_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/data-transports/aws/cloudwatch#step-2-set-up-an-iam-role`;
export const CWL_MANUALLY_SNS_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/data-transports/aws/cloudwatch#manual-iam-role-creation-additional-steps`;
export const LOG_ONBOARDING_SNS_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/data-transports/s3#configure-event-notifications-on-the-bucket`;
export const GCS_DATA_ONBOARDING_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/data-transports/gcs`;
export const HTTP_ONBOARDING_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/data-transports/http`;
// Doc page does not exist yet; the /otlp path will be created later.
export const OTLP_ONBOARDING_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/data-transports/otlp`;
export const PUB_SUB_DATA_ONBOARDING_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/data-transports/pubsub`;
export const AZURE_BLOB_DATA_ONBOARDING_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/data-transports/azure-blob-storage`;
export const AZURE_EVENT_HUB_DATA_ONBOARDING_DOC_URL = `${PANTHER_DOCS_LINK}/~/changes/2901/data-onboarding/data-transports/azure/event-hub`;
export const PRIVACY_DOC_URL = `${PANTHER_DOCS_LINK}/help/security-privacy#privacy`;
export const DATA_PROCESSING_URL = 'https://panther.com/data-processing-agreement';
export const SAAS_ONBOARDING_S3_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/data-transports/s3`;
export const SAAS_ONBOARDING_SQS_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/data-transports/sqs`;
export const SAAS_ONBOARDING_CLOUDWATCH_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/data-transports/cwl-source`;
export const SAAS_ONBOARDING_EVENTBRIDGE_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/data-transports/aws/eventbridge`;
export const SAAS_ONBOARDING_ASANA_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/asana`;
export const SAAS_ONBOARDING_AMAZON_SECURITY_LAKE_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/aws/security-lake`;
export const SAAS_ONBOARDING_ONELOGIN_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/onelogin`;
export const SAAS_ONBOARDING_SALESFORCE_REALTIME_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/salesforce/real-time-events`;
export const SAAS_ONBOARDING_BOX_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/box`;
export const SAAS_ONBOARDING_GSUITE_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/gsuite`;
export const SAAS_ONBOARDING_GITHUB_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/github`;
export const SAAS_ONBOARDING_ZENDESK_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/zendesk`;
export const SAAS_ONBOARDING_OKTA_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/okta`;
export const SAAS_ONBOARDING_DUO_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/duo`;
export const SAAS_ONBOARDING_1PASS_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/1password`;
export const SAAS_ONBOARDING_SALESFORCE_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/salesforce`;
export const SAAS_ONBOARDING_SLACK_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/slack`;
export const SAAS_ONBOARDING_ZOOM_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/zoom`;
export const SAAS_ONBOARDING_CROWDSTRIKE_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/crowdstrike/falcon-data-replicator`;
export const SAAS_ONBOARDING_M365_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/microsoft365`;
export const SAAS_ONBOARDING_ATLASSIAN_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/atlassian`;
export const SAAS_ONBOARDING_SNYK_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/snyk`;
export const SAAS_ONBOARDING_MICROSOFTGRAPH_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/microsoftgraph`;
export const SAAS_ONBOARDING_DROPBOX_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/dropbox`;
export const SAAS_ONBOARDING_MONGODB_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/mongodb`;
export const SAAS_ONBOARDING_SYSDIG_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/sysdig`;
export const SAAS_ONBOARDING_SENTINELONE_CLOUD_FUNNEL_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/sentinel-one#how-to-onboard-sentinelone-cloud-funnel-deep-visibility-logs-to-panther`;
export const SAAS_ONBOARDING_SENTINELONE_API_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/sentinel-one#how-to-onboard-sentinelone-api-activity-logs-to-panther`;
export const SAAS_ONBOARDING_BITWARDEN_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/bitwarden`;
export const SAAS_ONBOARDING_TINES_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/tines`;
export const SAAS_ONBOARDING_NETSKOPE_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/netskope`;
export const SAAS_ONBOARDING_TENABLE_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/tenable`;
export const SAAS_ONBOARDING_CARBON_BLACK_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/carbon-black`;
export const SAAS_ONBOARDING_PROOFPOINT_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/proofpoint`;
export const SAAS_ONBOARDING_WIZ_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/wiz`;
export const SAAS_ONBOARDING_RAPID7_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/rapid7`;
export const SAAS_ONBOARDING_SNOWFLAKE_AUDIT_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/snowflake`;
export const SAAS_ONBOARDING_CROWDSTRIKE_EVENT_STREAMS_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/crowdstrike/event-streams`;
export const SAAS_ONBOARDING_OPENAI_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/openai`;
export const SAAS_ONBOARDING_ANTHROPIC_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs/anthropic`;
export const SAAS_ONBOARDING_GENERAL_URL = `${PANTHER_DOCS_LINK}/data-onboarding/supported-logs`;
export const SAML_DOC_URL = `${PANTHER_DOCS_LINK}/system-configuration/saml`;
export const MOCKED_FUNCTIONS_DOC_URL = `${PANTHER_DOCS_LINK}/writing-detections/testing`;
export const WRITING_RULES_DOC_URL = `${PANTHER_DOCS_LINK}/writing-detections/rules`;
export const WRITING_POLICIES_DOC_URL = `${PANTHER_DOCS_LINK}/writing-detections/policies`;
export const WRITING_CORRELATION_RULES_DOC_URL = `${PANTHER_DOCS_LINK}/detections/correlation-rules`;
export const DATA_SCHEMAS_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/custom-log-types`;
export const ENABLING_FIELD_DISCOVERY_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/custom-log-types#enabling-field-discovery`;
export const REGEX_PARSER_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/custom-log-types/regex-parser`;
export const CSV_PARSER_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/custom-log-types/csv-parser`;
export const FASTMATCH_PARSER_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/custom-log-types/fastmatch-parser`;
export const SCRIPT_PARSER_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/custom-log-types/script-parser`;
export const PANTHERFLOW_DOCS_URL = `${PANTHER_DOCS_LINK}/pantherflow`;
export const PANTHERFLOW_VISUALIZATION_DOCS_URL = `${PANTHER_DOCS_LINK}/pantherflow/operators/visualize`;
export const QUERY_BUILDER_DOC_URL = `${PANTHER_DOCS_LINK}/search/search-tool`;
export const QUERY_HISTORY_DOC_URL = `${PANTHER_DOCS_LINK}/search/search-history`;
export const SAVED_QUERIES_CRON_DOC_URL = `${PANTHER_DOCS_LINK}/data-analytics/scheduled-queries`;
export const LUTS_S3_ROLE_DOC_URL = `${PANTHER_DOCS_LINK}/enrichment/lookup-tables#creating-an-iam-role`;
export const SNOWFLAKE_DOC_URL = `${PANTHER_DOCS_LINK}/system-configuration/snowflake-setup`;
export const USER_ROLES_DOC_URL = `${PANTHER_DOCS_LINK}/system-configuration/rbac`;
export const PUBLIC_API_DOC_URL = `${PANTHER_DOCS_LINK}/panther-developer-workflows/api`;
export const API_ROTATE_TOKEN_DOC_URL = `${PUBLIC_API_DOC_URL}#rotating-api-tokens`;
export const API_PLAYGROUND_DOC_URL = `${PANTHER_DOCS_LINK}/panther-developer-workflows/api/api-playground`;
export const BULK_UPLOADER_DOC_URL = `${PANTHER_DOCS_LINK}/panther-developer-workflows/panther-analysis-tool#uploading-to-panther`;
export const SCHEMAS_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/custom-log-types`;
export const SCHEMA_REFERENCE_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/custom-log-types/reference`;
export const TRANSFORMATIONS_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/custom-log-types/transformations`;
export const SCHEDULED_QUERY_LIMITS_DOC_URL = `${PANTHER_DOCS_LINK}/data-analytics/scheduled-queries`;
export const ENRICHMENT_PROVIDERS_DOC_URL = `${PANTHER_DOCS_LINK}/enrichment`;
export const ENRICHMENT_GCS_IDENTITY_DOC_URL = `${PANTHER_DOCS_LINK}/enrichment/custom#option-4-sync-custom-enrichment-data-from-a-google-cloud-storage-gcs-bucket`;
export const LOOKUP_TABLES_DOC_URL = `${PANTHER_DOCS_LINK}/enrichment/lookup-tables`;
export const RULE_FILTERS_DOC_URL = `${PANTHER_DOCS_LINK}/writing-detections/rules/rule-filters`;
export const RULE_UNIT_TESTS_DOC_URL = `${PANTHER_DOCS_LINK}/writing-detections/testing`;
export const LOG_SOURCE_FILTERS_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/filtering`;
export const LOG_SOURCE_RAW_FILTERS_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/ingestion-filters/raw-event`;
export const LOG_SOURCE_NORMALIZED_FILTERS_DOC_URL = `${PANTHER_DOCS_LINK}/data-onboarding/ingestion-filters/normalized-event`;
export const DETECTION_INHERITANCE_DOC_URL = `${PANTHER_DOCS_LINK}/detections/rules/inheritance`;
// TODO: replace with doc link once published (https://app.asana.com/0/1205453166036250/1206525621329534/f)
export const SIGNALS_DOC_URL = `${PANTHER_DOCS_LINK}/detections/correlation-rules/signals`;
// TODO: replace with doc link once AI Skills documentation is published
export const AI_SKILLS_DOC_URL = `${PANTHER_DOCS_LINK}/ai/skills`;

// End of doc URLs section

export const EVENTBRIDGE_ONBOARDING_DOC_URLS = {
  salesforceRealtime: SAAS_ONBOARDING_SALESFORCE_REALTIME_URL,
  onelogin: SAAS_ONBOARDING_ONELOGIN_DOC_URL,
};

export const DEFAULT_SMALL_PAGE_SIZE = 10;
export const DEFAULT_LARGE_PAGE_SIZE = 25;
export const DEFAULT_MAX_PAGE_SIZE = 999;

// Cron
export const DEFAULT_CRON_EXPRESSION = '0 0 * * *';
// The key under which User-related data will be stored in the storage
export const CIDR_BULK_INPUT_STORAGE_KEY = 'panther.apiTokens.cidrBulkInput';
export const USER_INFO_STORAGE_KEY = 'panther.user.info';
export const ANALYTICS_CONSENT_STORAGE_KEY = 'panther.generalSettings.analyticsConsent';
export const PANTHER_TIMEZONE_STORAGE_KEY = 'panther.generalSettings.timezone';
export const PANTHER_THEME_STORAGE_KEY = 'panther.generalSettings.theme';
export const PANTHER_SESSION_REDIRECT_KEY = 'panther.reauth.path';
// Session-storage key for the encrypted OAuth state blob during the remote
// MCP sign-in handoff. Written by GuardedRoute when it sees
// `mcp_auth=true&state=<blob>` in the URL; read by the /mcp-approval page
// on mount. Shared here so the framework-level GuardedRoute doesn't depend
// on a lazy-loaded feature page.
export const MCP_AUTH_STATE_KEY = 'mcp_auth_state';
export const PANTHER_LOG_SOURCES_TABLE_VIEW = 'panther.logSources.tableView';
export const PANTHER_SCHEMAS_TABLE_VIEW = 'panther.schemas.tableView';
export const PANTHER_DETECTIONS_TABLE_VIEW = 'panther.detections.tableView';
export const PANTHER_ENRICHMENTS_TABLE_VIEW = 'panther.enrichments.tableView';
export const PANTHER_DATA_MODELS_TABLE_VIEW = 'panther.dataModels.tableView';
export const PANTHER_DESTINATIONS_TABLE_VIEW = 'panther.destinations.tableView';
export const PANTHER_SEARCH_REGEX_ALERT_IS_DISMISSED_STORAGE_KEY =
  'panther.search.regexAlert.isDismissed';
export const PANTHER_DISPLAY_EVENT_JSON_PANTHER_FIELDS_STORAGE_KEY =
  'panther.eventJson.displayPantherFields';
export const PANTHER_EVENT_JSON_IS_EXPANDED_STORAGE_KEY = 'panther.eventJson.isExpanded';
export const PANTHER_SEARCH_TABLE_VIEW_STORAGE_KEY = 'panther.search.tableView';
export const PANTHER_AI_OUTPUT_LENGTH_STORAGE_KEY = 'panther.ai.outputLength';

// Maximum character length for organization profiles. Mirrors the Go constant
// MaxOrganizationProfileLength in api/lambda/ai/models/api.go.
export const MAX_ORGANIZATION_PROFILE_LENGTH = 8192;

export const PANTHER_HISTOGRAM_SIZE_STORAGE_KEY = 'panther.histogram.size';
export const PANTHER_HISTOGRAM_VISIBILITY_STORAGE_KEY = 'panther.histogram.visibility';

export type PermissionMapping<T = string> = Record<
  (typeof Permission)[keyof typeof Permission],
  {
    id: Permission;
    title: string;
    description: string;
    grouping: T;
    hidden?: boolean;
  }
>;

export const ROLE_PERMISSIONS: PermissionMapping<
  | 'Alerts'
  | 'Detections'
  | 'Cloud Security'
  | 'Analysis'
  | 'Integrations'
  | 'User Management'
  | 'Configuration'
  | 'Data'
  | 'Enrichment'
  | 'Panther AI'
> = {
  [Permission.RunPantherAi]: {
    id: Permission.RunPantherAi,
    title: 'Run Panther AI',
    description: 'Allows running Panther AI in both the Panther Console and API',
    grouping: 'Panther AI',
  },
  [Permission.AiRunAsModify]: {
    id: Permission.AiRunAsModify,
    title: 'AI Run As',
    description:
      'Allows configuring which user AI automations (auto-triage, scheduled prompts) execute as',
    grouping: 'Panther AI',
  },
  [Permission.ViewAiPrivateResponses]: {
    id: Permission.ViewAiPrivateResponses,
    title: 'View AI Private Responses',
    description: "Allows viewing all users' private AI responses",
    grouping: 'Panther AI',
  },
  [Permission.ManageAiResponses]: {
    id: Permission.ManageAiResponses,
    title: 'Manage AI Responses',
    description: "Allows editing, deleting, running, and bulk uploading any user's AI responses",
    grouping: 'Panther AI',
  },
  [Permission.ViewAiSkills]: {
    id: Permission.ViewAiSkills,
    title: 'View AI Skills',
    description: 'Allows viewing AI skills',
    grouping: 'Panther AI',
  },
  [Permission.ManageAiSkills]: {
    id: Permission.ManageAiSkills,
    title: 'Manage AI Skills',
    description: 'Allows creating, editing, and deleting AI skills',
    grouping: 'Panther AI',
  },
  [Permission.ViewScheduledPrompts]: {
    id: Permission.ViewScheduledPrompts,
    title: 'View Scheduled Prompts',
    description: 'Allows viewing AI scheduled prompts and their run history',
    grouping: 'Panther AI',
  },
  [Permission.ManageScheduledPrompts]: {
    id: Permission.ManageScheduledPrompts,
    title: 'Manage Scheduled Prompts',
    description:
      'Allows creating, editing, and deleting AI scheduled prompts (including via bulk upload of YAML files)',
    grouping: 'Panther AI',
  },
  [Permission.McpServerRead]: {
    id: Permission.McpServerRead,
    title: 'View & Connect to MCP Servers',
    description: 'Allows viewing and connecting to the configured MCP server integrations',
    grouping: 'Panther AI',
  },
  [Permission.McpServerModify]: {
    id: Permission.McpServerModify,
    title: 'Manage MCP Servers',
    description: 'Allows the creation, modification & deletion of MCP server integrations',
    grouping: 'Panther AI',
  },
  [Permission.UserRead]: {
    id: Permission.UserRead,
    title: 'View Users',
    description: 'Allows viewing the information of the Panther users',
    grouping: 'User Management',
  },
  [Permission.UserModify]: {
    id: Permission.UserModify,
    title: 'Manage Users',
    description:
      'Allows the modification, invitation & deletion of users from the platform. Also allows the creation of roles and the modification of role permissions. This permission grants Administrator level access to Panther.',
    grouping: 'User Management',
  },
  [Permission.AlertRead]: {
    id: Permission.AlertRead,
    title: 'View Alerts',
    description: 'Allows viewing of alerts in the platform',
    grouping: 'Alerts',
  },
  [Permission.AlertModify]: {
    id: Permission.AlertModify,
    title: 'Manage Alerts',
    description: 'Allows editing of alerts in the platform',
    grouping: 'Alerts',
  },
  [Permission.RuleRead]: {
    id: Permission.RuleRead,
    title: 'View Rules',
    description: 'Allows viewing the log rules setup in Panther',
    grouping: 'Detections',
  },
  [Permission.RuleModify]: {
    id: Permission.RuleModify,
    title: 'Manage Rules',
    description: 'Allows the modification & deletion of log rules',
    grouping: 'Detections',
  },
  [Permission.PolicyRead]: {
    id: Permission.PolicyRead,
    title: 'View Policies',
    description: 'Allows viewing the Cloud Accounts policies',
    grouping: 'Detections',
  },
  [Permission.PolicyModify]: {
    id: Permission.PolicyModify,
    title: 'Manage Policies',
    description: 'Allows editing & deleting policies for Cloud Accounts',
    grouping: 'Detections',
  },
  [Permission.BulkUpload]: {
    id: Permission.BulkUpload,
    title: 'Bulk Upload',
    description: 'Allows the bulk uploading of resources to the platform',
    grouping: 'Analysis',
  },
  [Permission.BulkUploadValidate]: {
    id: Permission.BulkUploadValidate,
    title: 'Bulk Upload Validate',
    description: 'Allows validation of bulk uploads',
    grouping: 'Analysis',
  },
  [Permission.ResourceRead]: {
    id: Permission.ResourceRead,
    title: 'Read Resources',
    description: 'Allows viewing the onboarded Cloud Account resources',
    grouping: 'Data',
  },
  [Permission.ResourceModify]: {
    id: Permission.ResourceModify,
    title: 'Modify Resources',
    description: 'Allows the suppression of misconfigured Cloud Account resources',
    grouping: 'Data',
  },
  [Permission.DataAnalyticsRead]: {
    id: Permission.DataAnalyticsRead,
    title: 'Run Log Searches',
    description: 'Allows querying historical log data',
    grouping: 'Data',
  },
  [Permission.DataAnalyticsModify]: {
    id: Permission.DataAnalyticsModify,
    title: 'Manage Saved Searches',
    description:
      'Allows the management of saved queries. Also allows the creation and management of custom dashboards.',
    grouping: 'Data',
  },
  [Permission.DestinationRead]: {
    id: Permission.DestinationRead,
    title: 'View Destinations',
    description: 'Allows viewing the configured Alert Destinations',
    grouping: 'Integrations',
  },
  [Permission.DestinationModify]: {
    id: Permission.DestinationModify,
    title: 'Manage Destinations',
    description: 'Allows the modification & deletion of Alert Destinations',
    grouping: 'Integrations',
  },
  [Permission.NotificationsSend]: {
    id: Permission.NotificationsSend,
    title: 'Send Notifications',
    description:
      'Allows sending outbound notifications (e.g. Slack messages) via configured destinations from Panther AI and related tools',
    grouping: 'Integrations',
  },
  [Permission.LogSourceRead]: {
    id: Permission.LogSourceRead,
    title: 'View Log Sources',
    description: 'Allows viewing the Log sources setup in Panther',
    grouping: 'Integrations',
  },
  [Permission.LogSourceModify]: {
    id: Permission.LogSourceModify,
    title: 'Manage Log Sources',
    description: 'Allows the management of Log sources',
    grouping: 'Integrations',
  },
  [Permission.LookupRead]: {
    id: Permission.LookupRead,
    title: 'View Lookups',
    description: 'Allows viewing the configured Lookups',
    grouping: 'Enrichment',
  },
  [Permission.LookupModify]: {
    id: Permission.LookupModify,
    title: 'Manage Lookups',
    description: 'Allows the modification & deletion of Lookups',
    grouping: 'Enrichment',
  },
  [Permission.CloudsecSourceRead]: {
    id: Permission.CloudsecSourceRead,
    title: 'View Cloud Security Sources',
    description: 'Allows viewing the connected Cloud Accounts in Panther',
    grouping: 'Integrations',
  },
  [Permission.CloudsecSourceModify]: {
    id: Permission.CloudsecSourceModify,
    title: 'Manage Cloud Security Sources',
    description: 'Allows the management of Cloud Accounts',
    grouping: 'Integrations',
  },
  [Permission.LogSourceRawDataRead]: {
    id: Permission.LogSourceRawDataRead,
    title: 'View Log Sources Raw Data',
    description: 'Allows access to the raw data of Log Sources',
    grouping: 'Integrations',
  },
  [Permission.GeneralSettingsRead]: {
    id: Permission.GeneralSettingsRead,
    title: 'Read Settings & SAML Preferences',
    description: 'Allows viewing the general platform settings & SAML preferences',
    grouping: 'Configuration',
  },
  [Permission.GeneralSettingsModify]: {
    id: Permission.GeneralSettingsModify,
    title: 'Edit Settings & SAML Preferences',
    description: 'Allows the alteration of general platform settings & SAML preferences',
    grouping: 'Configuration',
  },
  [Permission.SummaryRead]: {
    id: Permission.SummaryRead,
    title: 'View Overview',
    description: 'Allows viewing the Cloud Security & Log Analysis overview data',
    grouping: 'Configuration',
  },
  [Permission.OrganizationApiTokenModify]: {
    id: Permission.OrganizationApiTokenModify,
    title: 'Manage API Tokens',
    description: 'Allows management of the organization-wide Panther API tokens',
    grouping: 'Configuration',
  },
  [Permission.OrganizationApiTokenRead]: {
    id: Permission.OrganizationApiTokenRead,
    title: 'View API Tokens',
    description:
      'Allows viewing metadata about the Panther API tokens of the organization (no access to the token values)',
    grouping: 'Configuration',
  },
};

export const PANTHER_SAML_USER_PREFIX = 'PantherSSO';
export const PANTHER_SAML_ROLE_FROM_IDP_SUFFIX = ' (From IdP)';

export const SYSTEM_USER_ID = '00000000-0000-4000-8000-000000000000';

export const DEFAULT_SENSITIVE_VALUE = '*******************';

// The order of this slice determines the order of destinations on the new alert destination
// screen.
export const DESTINATIONS: Record<
  DestinationTypeEnum,
  {
    logo: string;
    logoLight?: string;
    title: string;
    shortTitle?: string;
    description?: string;
    type: DestinationTypeEnum;
  }
> = {
  [DestinationTypeEnum.Asana]: {
    logo: asanaLogo,
    title: 'Asana',
    description: 'Send Panther alerts to Asana for seamless security task management.',
    type: DestinationTypeEnum.Asana,
  },
  [DestinationTypeEnum.Sns]: {
    logo: snsLogo,
    title: 'AWS SNS',
    description: 'Route Panther alerts through AWS SNS for scalable custom notifications.',
    type: DestinationTypeEnum.Sns,
  },
  [DestinationTypeEnum.Sqs]: {
    logo: sqsLogo,
    title: 'AWS SQS',
    description: 'Deliver alerts to AWS SQS for flexible, reliable event processing.',
    type: DestinationTypeEnum.Sqs,
  },
  [DestinationTypeEnum.Blinkopswebhook]: {
    logo: blinkOpsLogo,
    title: 'Blink Ops',
    description: 'Trigger automated security workflows in Blink directly from Panther alerts.',
    type: DestinationTypeEnum.Blinkopswebhook,
  },
  [DestinationTypeEnum.Customwebhook]: {
    logo: customWebhook,
    logoLight: customWebhookLight,
    title: 'Custom Webhook',
    description: 'Forward alerts to any platform or service using a custom webhook.',
    type: DestinationTypeEnum.Customwebhook,
  },
  [DestinationTypeEnum.Discord]: {
    logo: discordLogo,
    title: 'Discord',
    description: 'Send real-time Panther alerts straight to your Discord security channels.',
    type: DestinationTypeEnum.Discord,
  },
  [DestinationTypeEnum.Expelwebhook]: {
    logo: expelLogo,
    title: 'Expel',
    description: 'Forward alerts to Expel for managed detection and continuous response.',
    type: DestinationTypeEnum.Expelwebhook,
  },
  [DestinationTypeEnum.Gcppubsub]: {
    logo: gcpPubSubLogo,
    title: 'GCP Pub/Sub',
    description: 'Publish alerts to GCP Pub/Sub for advanced, event-driven automation.',
    type: DestinationTypeEnum.Gcppubsub,
  },
  [DestinationTypeEnum.Github]: {
    logo: githubLogo,
    logoLight: githubLogoLight,
    title: 'Github',
    description: 'Automatically create GitHub issues from Panther alerts for faster triage.',
    type: DestinationTypeEnum.Github,
  },
  [DestinationTypeEnum.Incidentiowebhook]: {
    logo: incidentIoLogo,
    title: 'Incident.io',
    description: 'Push alerts to Incident.io for streamlined incident coordination and response.',
    type: DestinationTypeEnum.Incidentiowebhook,
  },
  [DestinationTypeEnum.Jira]: {
    logo: jiraLogo,
    title: 'Jira Cloud',
    description: 'Create and track Jira issues from alerts for efficient incident management.',
    type: DestinationTypeEnum.Jira,
  },
  [DestinationTypeEnum.Jiradatacenter]: {
    logo: jiraLogo,
    title: 'Jira Data Center',
    description: 'Send Panther alerts to your on-prem Jira Data Center instance securely.',
    type: DestinationTypeEnum.Jiradatacenter,
  },
  [DestinationTypeEnum.Mindflowwebhook]: {
    logo: mindflowLogo,
    logoLight: mindflowLogoLight,
    title: 'Mindflow',
    description: 'Trigger Mindflow automation workflows directly from Panther detections.',
    type: DestinationTypeEnum.Mindflowwebhook,
  },
  [DestinationTypeEnum.Msteams]: {
    logo: msTeamsLogo,
    title: 'Microsoft Teams',
    description: 'Instantly notify Teams channels when new security alerts are generated.',
    type: DestinationTypeEnum.Msteams,
  },
  [DestinationTypeEnum.Opsgenie]: {
    logo: opsgenieLogo,
    title: 'Opsgenie',
    description: 'Escalate Panther alerts to Opsgenie for reliable on-call incident handling.',
    type: DestinationTypeEnum.Opsgenie,
  },
  [DestinationTypeEnum.Pagerduty]: {
    logo: pagerDutyLogo,
    title: 'PagerDuty',
    description: 'Send critical Panther alerts to PagerDuty for fast incident escalation.',
    type: DestinationTypeEnum.Pagerduty,
  },
  [DestinationTypeEnum.Rapid7insightconnect]: {
    logo: rapid7ShortLogo,
    title: 'Rapid7 Insight Connect',
    shortTitle: 'Rapid7',
    description: 'Forward alerts to Rapid7 for extended detection and vulnerability response.',
    type: DestinationTypeEnum.Rapid7insightconnect,
  },
  [DestinationTypeEnum.Slackbot]: {
    logo: slackLogo,
    title: 'Slack Bot',
    description: 'Send rich, real-time alerts to your Slack workspace for faster collaboration.',
    type: DestinationTypeEnum.Slackbot,
  },
  [DestinationTypeEnum.Slack]: {
    logo: slackLogo,
    title: 'Slack Webhook',
    description: 'Integrate alerts via Slack webhook for flexible channel-based notifications.',
    type: DestinationTypeEnum.Slack,
  },
  [DestinationTypeEnum.Splunkwebhook]: {
    logo: splunkLogo,
    title: 'Splunk',
    description: 'Ingest Panther alerts into Splunk for centralized search and correlation.',
    type: DestinationTypeEnum.Splunkwebhook,
  },
  [DestinationTypeEnum.Tineswebhook]: {
    logo: tinesLogo,
    logoLight: tinesLogoLight,
    title: 'Tines',
    description: 'Send Panther alerts to Tines to orchestrate end-to-end incident response.',
    type: DestinationTypeEnum.Tineswebhook,
  },
  [DestinationTypeEnum.Torqwebhook]: {
    logo: torqLogo,
    logoLight: torqLogoLight,
    title: 'Torq',
    description: 'Trigger no-code Torq workflows from alerts to automate investigation steps.',
    type: DestinationTypeEnum.Torqwebhook,
  },
};

export const ALERT_VOLUMES: Record<string, { emblem: string }> = {
  extreme: { emblem: extremeAlertVolume },
  high: { emblem: highAlertVolume },
  low: { emblem: lowAlertVolume },
  none: { emblem: noneAlertVolume },
};

export const MANAGED_STATES: Record<string, { emblem: string; label: string }> = {
  account: { emblem: accountManaged, label: 'Team' },
  panther: { emblem: pantherManaged, label: 'Panther' },
  self: { emblem: selfManaged, label: 'Me' },
};

export const RULE_TYPES: Record<string, { emblem: string; label: string }> = {
  python: { emblem: pythonRule, label: 'Python Rule' },
  scheduled: { emblem: scheduledRule, label: 'Scheduled Rule' },
  simple: { emblem: simpleRule, label: 'Simple Rule' },
  correlation: { emblem: correlationRule, label: 'Correlation Rule' },
};

export const SEVERITY_TYPES_IN_ORDER = [
  Severity.Critical,
  Severity.High,
  Severity.Medium,
  Severity.Low,
  Severity.Info,
];

export const SEVERITY_COLOR_MAP_V3: { [key in Severity]: BadgeVariant } = {
  [Severity.Critical]: 'error-outline',
  [Severity.High]: 'warning-outline',
  [Severity.Medium]: 'medium-outline',
  [Severity.Low]: 'info-outline',
  [Severity.Info]: 'neutral-outline',
};

// TODO: confirm these colors
export const ENTITIES_COLOR_MAP_V3: {
  [key in DetectionType | 'GLOBAL' | 'DATA_MODEL' | 'SAVED_QUERY']: BadgeVariant;
} = {
  [DetectionType.Policy]: 'primary-outline',
  [DetectionType.Rule]: 'info-outline',
  [DetectionType.DerivedRule]: 'info-outline',
  [DetectionType.ScheduledRule]: 'accent-outline',
  [DetectionType.CorrelationRule]: 'accent-outline',
  SAVED_QUERY: 'accent-outline',
  GLOBAL: 'success-outline',
  DATA_MODEL: 'accent-outline',
};

export const ALERT_TYPE_COLOR_MAP_V3: {
  [key in AlertType]: (theme: MuiTheme) => string;
} = {
  // TODO: these colors might be updated in the future
  [AlertType.Rule]: theme => theme.palette.tokens.content.text.semantic.error.base,
  [AlertType.RuleError]: theme => theme.palette.tokens.content.text.accent[1].base,
  [AlertType.ScheduledRule]: theme => theme.palette.tokens.content.text.semantic.warning.base,
  [AlertType.ScheduledRuleError]: theme => theme.palette.tokens.content.text.semantic.suggest.base,
  [AlertType.Policy]: theme => theme.palette.tokens.content.text.semantic.info.base,
  [AlertType.SystemError]: theme => theme.palette.tokens.content.text.semantic.suggest[1],
  [AlertType.CorrelationRule]: theme => theme.palette.tokens.content.text.semantic.suggest[1],
  [AlertType.CorrelationRuleError]: theme => theme.palette.tokens.content.text.semantic.suggest[1],
};

export const NON_EDITABLE_ROLE_NAMES = ['Admin'];

export const MITRE_REPORT_KEY = 'MITRE ATT&CK';

export const UNASSIGNED_USER = 'unassigned';

export const STREAM_TYPES: Record<
  LogStreamType,
  { title: string; description: string; isBeta?: boolean }
> = {
  [LogStreamType.Auto]: {
    title: 'Auto',
    description: 'Log format is automatically detected.',
  },
  [LogStreamType.Lines]: { title: 'Lines', description: 'Events are line delimited.' },
  [LogStreamType.Json]: {
    title: 'JSON',
    description: 'Events are in JSON format.',
  },
  [LogStreamType.JsonArray]: {
    title: 'JSON Array',
    description: 'Events are in JSON Array format.',
  },
  [LogStreamType.CloudWatchLogs]: {
    title: 'CloudWatch Logs',
    description: 'Events are delivered to S3 from CloudWatch Logs.',
  },
  [LogStreamType.Xml]: {
    title: 'XML',
    description: 'Events are in XML format.',
    isBeta: true,
  },
};

export enum PageNameEnum {
  AIChat = 'Chat',
  AIChatHistory = 'Chat History',
  AIScheduledPrompts = 'Scheduled Prompts',
  AlertDestinations = 'Alert Destinations',
  Alerts = 'Alerts & Errors',
  CloudAccountNew = 'Connect a new Cloud Account',
  CloudAccounts = 'Cloud Accounts',
  ContentCatalog = 'Content Catalog',
  CloudResources = 'Cloud Resources',
  DataExplorer = 'Data Explorer',
  DataModelNew = 'New Data Model',
  DataModels = 'Data Models',
  SchemasNew = 'New Schema',
  Schemas = 'Schemas',
  DetectionEdit = 'Edit Detection',
  DetectionNew = 'New Detection',
  Detections = 'Detections',
  EnrichmentProviders = 'Enrichments',
  GlobalHelpers = 'Helpers',
  GlobalHelpersEdit = 'Edit Global Helper',
  GlobalHelpersNew = 'New Global Helper',
  LogSourcesNew = 'Add New Sources',
  LogSources = 'Log Sources',
  MitreAttack = 'MITRE ATT&CK®',
  MitreMapping = 'Map New Detections',
  Packs = 'Packs',
  QueryBuilder = 'Search',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values -- intentional alias
  Search = 'Search',
  SavedSearches = 'Saved Searches',
  SearchHistory = 'Search History',
  SettingsApiPlayground = 'API Playground',
  SettingsApiPlaygroundGraph = 'API Playground (GraphQL)',
  SettingsApiPlaygroundRest = 'API Playground (REST)',
  SettingsApiTokensNew = 'Create an API Token',
  SettingsApiTokens = 'API Tokens',
  SettingsGeneral = 'General',
  SettingsUsage = 'Usage',
  SettingsUsers = 'Users',
  SettingsUserRolesNew = 'Create a User Role',
  SettingsUserRoles = 'User Roles',
  SettingsAIAutomation = 'Panther AI',
  SettingsAlertContextTags = 'Alert Context Tags',
  SettingsAISkills = 'AI Skills',
  SettingsAISkillsNew = 'Create AI Skill',
  Support = 'Support',
}

export const GREYNOISE_LOOKUP_TABLE_KEY_NAMES = [
  'greynoise_noise_advanced',
  'greynoise_noise_basic',
  'greynoise_riot_basic',
  'greynoise_riot_advanced',
];

export const IPINFO_LOOKUP_TABLE_KEY_NAMES = ['ipinfo_location', 'ipinfo_asn', 'ipinfo_privacy'];

// These should match the definitions in deployments/pulumi/src/helpers/const.py
export enum DataLakeType {
  Databricks = 'databricks',
  Redshift = 'rss',
  SnowflakeManaged = 'snowflake-managed',
  SnowflakeConnected = 'connected',
  SnowflakeSelfHosted = 'snowflake-self-hosted',
}

// The default name of the Panther logs database in Athena and Snowflake
export const DEFAULT_LOGS_DB_NAME_SNOWFLAKE = 'panther_logs.public';
export const DEFAULT_LOGS_DB_NAME_DATABRICKS = 'panther_logs';
export const DEFAULT_RULE_MATCHES_DB_NAME_SNOWFLAKE = 'panther_rule_matches.public';
export const DEFAULT_RULE_ERRORS_DB_NAME_SNOWFLAKE = 'panther_rule_errors.public';
export const DEFAULT_VIEWS_DB_NAME_SNOWFLAKE = 'panther_views.public';

// Key names of all the panther managed 3rd party enrichment sources
export const PANTHER_MANAGED_LOOKUP_TABLE_KEY_NAMES = [
  ...GREYNOISE_LOOKUP_TABLE_KEY_NAMES,
  ...IPINFO_LOOKUP_TABLE_KEY_NAMES,
];

export const ACTOR_NAME_UNKNOWN = 'Unknown';

// This enum represents how we display a Saved Search's type in the UI.
export enum SavedQueryQueryKindsEnum {
  QueryBuilder = 'Search',
  NativeSql = 'Native SQL',
  PantherFlow = 'PantherFlow Search',
}

export const MAX_DOWNLOADABLE_ROWS_FOR_CSV = 1_000;

export const supportLinks = {
  email: 'support@panther.io',
  demo: 'https://runpanther.io/request-a-demo/',
};

export const integrationRequests = {
  email: 'integration-requests@panther.com',
};

// Analysis Spec Versions
export enum AnalysisSpecVersion {
  Unknown = '',
  V1 = 'v1',
  V2 = 'v2',
}

export const ALERT_STATUS_IN_ORDER: AlertStatus[] = [
  AlertStatus.Open,
  AlertStatus.Triaged,
  AlertStatus.Resolved,
  AlertStatus.Closed,
];
